import mortgageRateMeme from "@/assets/mortgage-rate-meme.webp";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import IdxWidget from "@/components/IdxWidget";
import PropertySearchBar from "@/components/PropertySearchBar";
import { IDX } from "@/config/idx";
import {
  ArrowRight,
  Home,
  Key,
  TrendingUp,
  CalendarCheck,
  Quote,
  Video,
  Plane,
  Sparkles,
  Handshake,
  MapPin,
} from "lucide-react";
import QuickInquiryDialog from "@/components/QuickInquiryDialog";
import NewsletterBanner from "@/components/NewsletterBanner";
import { BlogCardGrid } from "@/components/BlogCard";
import { useBlogPosts } from "@/lib/blog";

import NJCommunitiesCarousel from "@/components/NJCommunitiesCarousel";
import heroImg from "@/assets/nj-luxury-hero.jpg";
import portrait from "@/assets/john-mastroianni-portrait.webp";
import njMapBg from "@/assets/nj-map-bg.webp";
import wMarriottHero from "@/assets/wpompano/crown-view.jpg.asset.json";

const Index = () => {
  const { posts } = useBlogPosts();
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="John Mastroianni | Align Right Realty - Homekey — Central NJ Luxury Real Estate"
        description="Luxury homes for sale in Princeton, West Windsor, Hamilton, Robbinsville, Lawrenceville, Trenton and across Mercer, Middlesex, Monmouth & Ocean counties. 40+ years of New Jersey real estate expertise."
        path="/"
      />
      <Navigation transparentOnTop />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        <img
          src={heroImg}
          alt="Luxury New Jersey estate home at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 container mx-auto max-w-[1400px] px-6 pt-36 md:pt-44 pb-16 md:pb-28">
          <div className="max-w-6xl mx-auto text-center">
            <p className="eyebrow font-semibold text-brand-ivory mb-2 md:mb-3 pt-6 md:pt-10">I am your local realtor</p>
            <p className="eyebrow font-semibold text-brand-ivory mb-6">Central New Jersey · The Shore · Statewide</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] font-light text-white leading-[1.05] tracking-tight mb-6 md:mb-8 lg:mb-5 text-balance">
              Experience matters{" "}
              <span className="italic text-brand-ivory">in every move.</span>
            </h1>
            <div className="max-w-5xl lg:max-w-[1280px] mx-auto mb-8 md:mb-10 lg:mb-7 space-y-3">
              <p className="text-base md:text-lg lg:text-[0.95rem] text-white/85 font-light leading-relaxed">
                We move beyond outdated, one-size-fits-all real estate marketing
                methods. Instead, by using up to six generative AI-powered
                marketing platforms, we connect with potential buyers who might
                otherwise miss your listing. From creating an attention-grabbing
                first impression—building genuine interest, to inspiring an
                online visit; &amp; ultimately motivating them to schedule an
                in-person visit. We help guide buyers every step of the way.
              </p>
              <p className="text-base md:text-lg lg:text-[0.95rem] text-white/85 font-light leading-relaxed">
                Forty years of trusted representation as a real estate agent across 14 of the 21
                counties of NJ, including—but not limited to—Mercer, Middlesex,
                Monmouth &amp; Ocean Counties. John Mastroianni guides New Jersey
                buyers and sellers with patience, creativity, and an unwavering
                commitment to getting it right.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/buy"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 sm:px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all"
              >
                Find Your Home <ArrowRight className="h-4 w-4" />
              </Link>
              <QuickInquiryDialog
                trigger={
                  <button
                    type="button"
                    className="inline-flex items-center gap-3 border border-white/40 text-white px-6 sm:px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-foreground transition-all"
                  >
                    <CalendarCheck className="h-4 w-4" /> Request a Showing
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Search band */}
      <section className="py-14 md:py-20 bg-secondary/40 border-b border-border">
        <div className="container mx-auto max-w-[1200px] px-6 lg:grid lg:grid-cols-5 lg:gap-12 lg:items-center">
          <div className="lg:col-span-3">
            <div className="text-center lg:text-left mb-8">
              <p className="eyebrow mb-4">Property Search</p>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground leading-tight">
                Find your <span className="italic text-primary">New Jersey home.</span>
              </h2>
            </div>
            <PropertySearchBar />
          </div>
          <div className="lg:col-span-2 mt-10 lg:mt-0 flex justify-center">
            <div className="max-w-[420px] w-full lg:w-auto border border-border bg-background p-2 rounded-lg shadow-md">
              <img
                src={mortgageRateMeme}
                alt="Meme: waiting for the bubble to pop or rates to drop since 2017"
                width={612}
                height={755}
                loading="lazy"
                className="w-full h-auto lg:w-auto lg:max-h-[340px] object-contain rounded-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro / Philosophy */}
      <section className="py-20 md:py-28 texture-paper">
        <div className="container mx-auto max-w-[1100px] px-6 text-center">
          <p className="eyebrow mb-6">Welcome to Align Right Realty - Homekey</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight mb-8">
            Where every client is a priority,
            <span className="italic text-primary"> and every deal a milestone.</span>
          </h2>
          <div className="gold-divider mx-auto mb-8" />
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-light max-w-3xl mx-auto">
            With over four decades of New Jersey real estate experience, John is
            your trusted partner through every chapter — first home, dream home,
            or generational sale. Each journey is shaped around your goals, your
            timeline, and the life you're building. You're never just a
            transaction; you're a neighbor.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-20 md:py-32 border-y border-primary/20 overflow-hidden texture-linen">
        {/* Layered copper wash + paper texture */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 10% 0%, hsl(var(--brand-gold) / 0.18), transparent 55%), radial-gradient(ellipse at 90% 100%, hsl(var(--brand-gold-deep) / 0.22), transparent 60%), linear-gradient(180deg, hsl(34 25% 91%) 0%, hsl(34 22% 88%) 100%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        />
        <div
          aria-hidden="true"
          className="hidden md:block absolute -left-32 top-1/3 w-[360px] h-[360px] rounded-full bg-primary/10 blur-3xl pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="hidden md:block absolute -right-24 -bottom-24 w-[320px] h-[320px] rounded-full border border-primary/20 pointer-events-none"
        />

        <div className="relative container mx-auto max-w-[1400px] px-6">
          <div className="text-center mb-16 md:mb-20">
            <p className="eyebrow mb-4 inline-flex items-center justify-center gap-3 text-primary">
              <span className="h-px w-8 bg-primary/60" />
              Services Tailored to Your Needs
              <span className="h-px w-8 bg-primary/60" />
            </p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground">
              Comprehensive
              <span className="italic text-primary"> Real Estate Solutions.</span>
            </h2>
            <div className="gold-divider mx-auto mt-8" />
          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-primary/15">
            {[
              {
                icon: Video,
                title: "3D Video Walkthroughs",
                desc: "Cinematic, immersive tours that let qualified buyers experience your home from anywhere in the country.",
              },
              {
                icon: Plane,
                title: "Drone Videography",
                desc: "Sweeping aerial cinematography that frames your home, lot, and neighborhood at their most enviable.",
              },
              {
                icon: Sparkles,
                title: "Staging Assistance",
                desc: "Hands-on guidance and trusted partners to make every room read like a magazine before the first showing.",
              },
              {
                icon: Handshake,
                title: "Creative Negotiation",
                desc: "Four decades of dealmaking refined into instinct — the difference between a good outcome and a great one.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="relative bg-[hsl(34_22%_94%)] texture-paper p-10 md:p-12 shadow-[0_2px_18px_-12px_hsl(220_18%_14%/0.18)] hover:bg-[hsl(34_24%_91%)] hover:shadow-[0_14px_36px_-18px_hsl(38_72%_62%/0.2)] transition-all duration-500 group overflow-hidden"
              >
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
                <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 border border-primary/25 text-primary mb-8 group-hover:bg-primary/15 group-hover:border-primary/40 transition-colors duration-500">
                  <Icon className="h-6 w-6" strokeWidth={1.25} />
                </span>
                <h3 className="font-serif text-xl md:text-2xl font-light text-foreground mb-4">{title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed text-sm md:text-base">{desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Editorial split — Meet John */}
      <section className="py-20 md:py-32 texture-paper">
        <div className="container mx-auto max-w-[1400px] px-6">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="relative">
              <div className="bg-secondary/60 aspect-[4/5] flex items-end justify-center overflow-hidden">
                <img
                  src={portrait}
                  alt="John Mastroianni of Align Right Realty - Homekey — luxury real estate expert serving Mercer, Middlesex, Monmouth & Ocean counties"
                  className="w-full h-full object-contain object-bottom"
                  width={800}
                  height={1000}
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-6 md:p-8 hidden md:block">
                <div className="text-3xl md:text-4xl font-serif font-light">40+</div>
                <div className="text-[10px] tracking-[0.3em] uppercase mt-1">Years Experience</div>
              </div>
            </div>
            <div>
              <p className="eyebrow mb-6">Meet John</p>
              <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight mb-8">
                Where experience meets
                <span className="italic text-primary"> innovation.</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                John Mastroianni has spent forty years quietly mastering the
                New Jersey market — from first-time buyers in Hamilton to
                generational sellers in Princeton. His reputation is built on
                patience, candor, and a refusal to cut corners on behalf of the
                people who trust him.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10 italic">
                "I wouldn't sell you a house I wouldn't sell to my own kids."
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 text-primary text-xs tracking-[0.3em] uppercase border-b border-primary pb-2 hover:gap-5 transition-all"
              >
                Read John's Story <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Communities */}
      <section className="relative py-20 md:py-28 bg-secondary/40 border-t border-border overflow-hidden">
        <img
          src={njMapBg}
          alt="Illustrated line map of New Jersey"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
          width={1920}
          height={1024}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 via-transparent to-secondary/60 pointer-events-none" />
        <div className="relative container mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">Featured Communities</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground">
              Four counties.<span className="italic text-primary"> One trusted guide.</span>
            </h2>
          </div>
          <ul className="flex flex-wrap justify-center gap-x-8 sm:gap-x-10 gap-y-4 text-muted-foreground font-light">
            {[
              "Princeton","West Windsor","Hamilton","Robbinsville","Lawrenceville",
              "Trenton","East Windsor","Plainsboro","Cranbury","Spotswood",
              "Mercer County","Middlesex County","Monmouth County","Ocean County",
            ].map((city) => (
              <li key={city} className="text-lg sm:text-xl">{city}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Lifestyle carousel */}
      <NJCommunitiesCarousel />

      {/* W Marriott Feature */}
      <section className="relative overflow-hidden bg-brand-charcoal-section text-brand-ivory">
        <img
          src={wMarriottHero.url}
          alt="W Marriott Residences Pompano Beach — oceanfront luxury tower at sunset"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal-section via-brand-charcoal-section/80 to-brand-charcoal-section/30" />
        <div className="relative container mx-auto max-w-[1400px] px-6 py-24 md:py-36">
          <div className="max-w-2xl">
            <p className="eyebrow mb-6 flex items-center gap-3 text-base md:text-lg tracking-[0.35em]" style={{ color: "hsl(42 70% 68%)" }}>
              <span className="h-px w-10" style={{ backgroundColor: "hsl(42 70% 68%)" }} /> Now Selling · Pompano Beach, FL
            </p>
            <h2 className="font-serif text-5xl md:text-7xl font-light leading-[1.08] mb-8 text-white">
              Come live or invest at
              <br />
              <span className="italic font-normal" style={{ color: "hsl(42 78% 66%)" }}>W Marriott</span>
              <br />
              Luxury residences and living.
            </h2>
            <p className="text-white font-light text-lg md:text-xl leading-relaxed mb-6 max-w-xl">
              Luxury waterfront living meets an exceptional investment opportunity. Oceanfront
              private residences and Marriott-branded condo-hotel suites — with construction
              breaking ground October 2026 and estimated completion in 2029.
            </p>
            <ul className="space-y-2 text-white/90 font-light text-base mb-10">
              <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-primary" /> Founding-buyer pricing available now</li>
              <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-primary" /> Private residences, penthouses, and condo-hotel suites</li>
              <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-primary" /> Private tours available with a dedicated representative</li>
            </ul>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/w-pompano-beach"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all shadow-[var(--shadow-gold)]"
              >
                Explore W Marriott <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/w-pompano-beach#floor-plans"
                className="inline-flex items-center gap-3 border border-brand-ivory/40 text-brand-ivory px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-brand-ivory hover:text-foreground transition-all"
              >
                View Floor Plans
              </Link>
              <Link
                to="/w-pompano-beach#inquire"
                className="inline-flex items-center gap-3 border border-brand-ivory/40 text-brand-ivory px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-brand-ivory hover:text-foreground transition-all"
              >
                <CalendarCheck className="h-4 w-4" /> Schedule a Private Tour
              </Link>
            </div>
          </div>
        </div>
      </section>




      {/* Relocation / Referral */}
      <section className="relative py-20 md:py-28 border-y border-primary/20 overflow-hidden bg-brand-charcoal-section text-brand-ivory">
        {/* Layered copper gradient + texture */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(ellipse at 15% 30%, hsl(var(--brand-gold) / 0.35), transparent 55%), radial-gradient(ellipse at 85% 80%, hsl(var(--brand-gold-deep) / 0.45), transparent 60%), linear-gradient(135deg, hsl(var(--brand-charcoal-section)) 0%, hsl(var(--brand-charcoal-light)) 100%)",
          }}
        />
        <div className="absolute inset-0 texture-linen opacity-40 pointer-events-none" />
        {/* Decorative copper rings */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full border border-primary/30"
        />
        <div
          aria-hidden="true"
          className="hidden md:block absolute -right-16 -top-16 w-[280px] h-[280px] rounded-full border border-primary/20"
        />
        <div
          aria-hidden="true"
          className="hidden md:block absolute -left-24 -bottom-24 w-[300px] h-[300px] rounded-full bg-primary/10 blur-3xl"
        />

        <div className="relative container mx-auto max-w-[1100px] px-6">
          <div className="grid md:grid-cols-3 gap-10 md:gap-14 items-center">
            <div className="md:col-span-2">
              <p className="eyebrow text-primary mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-primary" />
                Relocation &amp; Referrals
              </p>
              <h2 className="font-serif text-3xl md:text-5xl font-light leading-tight mb-6 text-brand-ivory">
                Buying or selling
                <span className="italic text-primary"> outside New Jersey?</span>
              </h2>
              <p className="text-brand-ivory/75 font-light leading-relaxed md:text-lg max-w-2xl">
                If you're relocating across state lines, John's national network
                pairs you with a vetted, top-producing agent in your destination
                market — so you'll be just as well represented there as you are
                here.
              </p>
            </div>
            <div className="md:text-right">
              <Link
                to="/contact"
                className="group relative inline-flex items-center gap-3 overflow-hidden bg-primary text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase transition-all duration-500 shadow-[var(--shadow-gold)] hover:shadow-[0_10px_32px_-12px_hsl(34_42%_38%/0.22)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-charcoal"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      "linear-gradient(120deg, hsl(22 52% 50%) 0%, hsl(28 60% 55%) 50%, hsl(18 42% 38%) 100%)",
                  }}
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 mix-blend-overlay texture-linen"
                />
                <span className="relative inline-flex items-center gap-3">
                  Get Referred <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 texture-paper">
        <div className="container mx-auto max-w-[1200px] px-6">
          <div className="text-center mb-16 md:mb-20">
            <p className="eyebrow mb-4">Client Voices</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
              Treated like family,
              <span className="italic text-primary"> represented like royalty.</span>
            </h2>
            <div className="gold-divider mx-auto mt-8" />
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border">
            {[
              {
                quote:
                  "John gives 100% to every client. Down to earth, always available, and his advice is consistently on point. He brings something unique to the table that makes him stand out.",
                name: "Rushal P.",
                role: "Buyer",
              },
              {
                quote:
                  "John supported us every step of the way. As first-time home buyers, his patience and clear information turned what could have been stressful into something we genuinely enjoyed.",
                name: "Dan K.",
                role: "First-time Buyer",
              },
              {
                quote:
                  "John was extremely knowledgeable about the area and pricing, available whenever we needed him. He and his team made buying a home completely stress-free.",
                name: "Elisa",
                role: "Buyer",
              },
              {
                quote:
                  "We searched for a long time before meeting John. Within weeks he showed us a home we loved and walked us through closing. I highly recommend him.",
                name: "N. M.",
                role: "Buyer",
              },
              {
                quote:
                  "John keeps it real about the market. He'll tell you when to move and when to wait — and that patience pays. Highly recommended.",
                name: "Benjamin C.",
                role: "Buyer",
              },
              {
                quote:
                  "His motto — 'I wouldn't sell you a house I wouldn't sell to my own kids' — was reassuring from day one. Our home is lovely. We can't thank him enough.",
                name: "Julio C.",
                role: "First-time Buyer",
              },
            ].map((t) => (
              <figure
                key={t.name}
                className="bg-background p-10 md:p-12 flex flex-col"
              >
                <Quote className="h-7 w-7 text-primary mb-6" strokeWidth={1.25} />
                <blockquote className="text-foreground font-light leading-relaxed text-base mb-8 flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption>
                  <div className="font-serif text-lg text-foreground">{t.name}</div>
                  <div className="eyebrow-sm text-muted-foreground mt-1">
                    {t.role}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Homes (IDX Showcase) */}
      <section className="py-20 md:py-28 texture-paper border-t border-border">
        <div className="container mx-auto max-w-[1200px] px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">Curated Portfolio</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
              Featured <span className="italic text-primary">Homes</span>
            </h2>
            <div className="gold-divider mx-auto mt-8" />
          </div>
          <IdxWidget
            widgetId={IDX.widgets.featuredCarousel}
            label="Featured Listings"
            minHeight={460}
            className="bg-background border border-border rounded-sm p-4 md:p-6"
          />
          <div className="mt-10 flex justify-center">
            <Link to="/listings" className="btn-luxury">
              View All Listings
            </Link>
          </div>
        </div>
      </section>

      {/* Latest from the Blog */}
      <section className="py-20 md:py-28 texture-paper border-t border-border">
        <div className="container mx-auto max-w-[1200px] px-6">
          <div className="text-center mb-12">
            <p className="eyebrow text-primary mb-4">From the Blog</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
              Real estate news that <span className="italic text-primary">moves with the market.</span>
            </h2>
            <p className="mt-6 text-lg font-light text-foreground/80 max-w-2xl mx-auto leading-relaxed">
              Expert housing-market updates and buyer &amp; seller guidance, refreshed regularly — so you
              always make your next move with clarity.
            </p>
            <div className="gold-divider mx-auto mt-8" />
          </div>

          <BlogCardGrid posts={posts.slice(0, 3)} />

          <div className="mt-12 flex justify-center">
            <Link to="/blog" className="btn-luxury">
              Read the Blog
            </Link>
          </div>
        </div>
      </section>

      <NewsletterBanner />


      {/* CTA */}
      <section className="py-20 md:py-32 bg-brand-charcoal-section text-brand-ivory">
        <div className="container mx-auto max-w-[900px] px-6 text-center">
          <p className="eyebrow text-primary mb-6">Begin Your Journey</p>
          <h2 className="font-serif text-3xl md:text-6xl font-light leading-tight mb-8">
            Turn your real estate goals
            <span className="italic"> into reality.</span>
          </h2>
          <p className="text-brand-ivory/70 font-light text-base md:text-lg mb-10 leading-relaxed">
            You're not just another transaction — you're a valued part of
            John's real estate family.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <QuickInquiryDialog
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all"
                >
                  <CalendarCheck className="h-4 w-4" /> Request a Showing
                </button>
              }
            />
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-ivory/30 text-brand-ivory px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase hover:bg-brand-ivory hover:text-foreground transition-all"
            >
              Full Contact <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
